import React, { useEffect, useLayoutEffect, useMemo, useState, useRef, useCallback } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls, Html } from "@react-three/drei";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader";
import { Button, Spin, theme as antdTheme, Typography } from "antd";
import { Expand } from "lucide-react";
import Icon from "./Icon";
import * as THREE from "three";
import { convertToSignedUrl } from "../utils/wasabiHelper";
import Hotspot from "../page/ModuleViewer/components/Hotspot";

const draco = new DRACOLoader();
draco.setDecoderPath("https://www.gstatic.com/draco/versioned/decoders/1.5.6/");

function ModelLoadingIndicator({ inCanvas = false }) {
  const content = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <style>
        {`
          .airsmart-model-spin,
          .airsmart-model-spin .ant-spin-dot-holder {
            color: #111827 !important;
          }
          .airsmart-model-spin .ant-spin-dot-item {
            background-color: #111827 !important;
          }
        `}
      </style>
      <Spin className="airsmart-model-spin" />
    </div>
  );

  if (inCanvas) {
    return (
      <Html center zIndexRange={[10, 0]}>
        {content}
      </Html>
    );
  }

  return content;
}

function Model({
  url,
  hiddenLayers,
  onLoadComplete,
  errorColor,
  secondaryTextColor,
}) {
  const [gltf, setGltf] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!url) {
      setGltf(null);
      setLoading(false);
      setError(null);
      onLoadComplete?.(false);
      return;
    }
    const loader = new GLTFLoader();
    loader.setDRACOLoader(draco);
    let cancelled = false;
    setLoading(true);
    setError(null);
    setGltf(null);
    onLoadComplete?.(false);

    loader.load(
      url,
      (g) => {
        if (cancelled) return;
        const box = new THREE.Box3().setFromObject(g.scene);
        const size = box.getSize(new THREE.Vector3()).length();
        const scale = size > 0 ? 5 / size : 1;
        const center = box.getCenter(new THREE.Vector3());
        g.scene.scale.setScalar(scale);
        g.scene.position.sub(center.multiplyScalar(scale));
        setGltf(g);
        setLoading(false);
        onLoadComplete?.(true);
      },
      undefined,
      (err) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error("[fe LiveModel3DPreview] glTF load failed:", err);
        setError(err);
        setLoading(false);
        onLoadComplete?.(false);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [url, onLoadComplete]);

  useEffect(() => {
    if (!gltf?.scene || !hiddenLayers) return;
    const hidden = new Set(hiddenLayers);
    gltf.scene.traverse((obj) => {
      if (!obj.isMesh) return;
      obj.visible = !(hidden.has(obj.name) || hidden.has(obj.uuid));
    });
  }, [gltf, hiddenLayers]);

  if (loading) {
    return <ModelLoadingIndicator inCanvas />;
  }

  if (error) {
    return (
      <Html center zIndexRange={[10, 0]}>
        <div
          style={{
            color: errorColor,
            padding: 24,
            fontSize: 14,
            textAlign: "center",
            minWidth: 250,
            backdropFilter: "blur(4px)",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 16 }}>
            Failed to load 3D model
          </div>
          <div style={{ fontSize: 13, color: secondaryTextColor, lineHeight: 1.4 }}>
            Please check the model URL and CORS settings
          </div>
        </div>
      </Html>
    );
  }

  if (!gltf) return null;
  return <primitive object={gltf.scene} />;
}

function CameraController({ cameraSettings, controlsRef }) {
  const { camera } = useThree();

  useLayoutEffect(() => {
    if (!cameraSettings) return;

    if (cameraSettings.position) {
      camera.position.set(...cameraSettings.position);
    }
    if (cameraSettings.fov !== undefined) camera.fov = cameraSettings.fov;
    if (cameraSettings.zoom !== undefined) camera.zoom = cameraSettings.zoom;

    if (controlsRef.current && cameraSettings.target) {
      controlsRef.current.target.set(...cameraSettings.target);
      controlsRef.current.update();
    }

    camera.updateProjectionMatrix();
  }, [camera, cameraSettings, controlsRef]);

  return null;
}

function HotspotsRenderer({ hotspots }) {
  if (!Array.isArray(hotspots) || hotspots.length === 0) return null;
  return (
    <>
      {hotspots.map((hotspot, index) => (
        <Hotspot
          key={hotspot.id || index}
          hotspot={hotspot}
          index={index}
        />
      ))}
    </>
  );
}

/**
 * LiveModel3DPreview — inline interactive 3D viewer for the learner.
 * Renders with saved camera / hotspots / hidden layers. Overlay button
 * fires onOpenFullscreen(mediaId) to enter the dedicated fullscreen route.
 */
const LiveModel3DPreview = ({
  media: mediaProp,
  mediaId,
  mediaResolver,
  onOpenFullscreen,
  height = 360,
  fullscreenLabel = "Open fullscreen",
}) => {
  const { token } = antdTheme.useToken();
  const media = mediaProp || (mediaResolver ? mediaResolver(mediaId) : null);
  const wrapperRef = useRef(null);
  const controlsRef = useRef(null);
  const modelReadyTimerRef = useRef(null);
  const [modelReady, setModelReady] = useState(false);

  const url = useMemo(() => {
    if (!media?.url) return null;
    if (typeof media.url === "string" && media.url.includes("wasabisys.com")) {
      return convertToSignedUrl(media.url) || media.url;
    }
    return media.url;
  }, [media?.url]);

  useEffect(() => {
    if (modelReadyTimerRef.current) {
      clearTimeout(modelReadyTimerRef.current);
    }
    setModelReady(false);
  }, [url]);

  const handleModelLoadComplete = useCallback((ok) => {
    if (modelReadyTimerRef.current) {
      clearTimeout(modelReadyTimerRef.current);
    }
    if (!ok) {
      setModelReady(false);
      return;
    }
    modelReadyTimerRef.current = setTimeout(() => {
      setModelReady(true);
    }, 500);
  }, []);

  useEffect(() => {
    return () => {
      if (modelReadyTimerRef.current) {
        clearTimeout(modelReadyTimerRef.current);
      }
    };
  }, []);

  if (!media) {
    return (
      <div
        style={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          color: token.colorTextSecondary,
        }}
      >
        <ModelLoadingIndicator />
      </div>
    );
  }

  if (!media.url) {
    return (
      <div
        style={{
          height,
          padding: 16,
          borderRadius: 10,
          border: `1px dashed ${token.colorBorderSecondary}`,
          color: token.colorTextTertiary,
        }}
      >
        3D model unavailable ({mediaId})
      </div>
    );
  }

  const camera = media.camera || { position: [5, 5, 5], target: [0, 0, 0], fov: 50 };
  const hiddenLayers = (media.layers || [])
    .filter((l) => l.visible === false)
    .flatMap((l) => [l.name, l.uuid].filter(Boolean));

  return (
    <div
      ref={wrapperRef}
      style={{
        position: "relative",
        margin: "16px 0",
        height,
        borderRadius: 12,
        overflow: "hidden",
        border: `1px solid ${token.colorBorderSecondary}`,
        background: token.colorBgContainer,
      }}
    >
      <Canvas
        shadows
        camera={{ position: camera.position, fov: camera.fov || 50 }}
        style={{ height: "100%", width: "100%" }}
        gl={{ antialias: true, alpha: true }}
      >
        <ambientLight intensity={1} />
        <directionalLight position={[10, 10, 5]} intensity={1} castShadow />
        <Model
          url={url}
          hiddenLayers={hiddenLayers}
          onLoadComplete={handleModelLoadComplete}
          errorColor={token.colorError}
          secondaryTextColor={token.colorTextSecondary}
        />
        {modelReady && (
          <>
            <OrbitControls
              ref={controlsRef}
              target={camera.target || [0, 0, 0]}
              makeDefault
              enableDamping
              dampingFactor={0.05}
              rotateSpeed={1.2}
              zoomSpeed={1.2}
              panSpeed={1.0}
              autoRotate={false}
              minDistance={0.5}
              maxDistance={50}
              minPolarAngle={0}
              maxPolarAngle={Math.PI}
              enableKeys
              keyPanSpeed={7.0}
              screenSpacePanning={false}
              touches={{
                ONE: THREE.TOUCH.ROTATE,
                TWO: THREE.TOUCH.DOLLY_PAN,
              }}
              mouseButtons={{
                LEFT: THREE.MOUSE.ROTATE,
                MIDDLE: THREE.MOUSE.DOLLY,
                RIGHT: THREE.MOUSE.PAN,
              }}
            />
            <CameraController cameraSettings={camera} controlsRef={controlsRef} />
            <HotspotsRenderer hotspots={media.hotspots} />
          </>
        )}
      </Canvas>
      <div
        style={{
          position: "absolute",
          top: 12,
          left: 12,
          padding: "4px 10px",
          borderRadius: 6,
          background: "rgba(0,0,0,0.55)",
          color: "#fff",
          fontSize: 12,
          fontWeight: 600,
          pointerEvents: "none",
        }}
      >
        3D {media.fileName || (mediaId || "").slice(0, 8)}
        {Array.isArray(media.hotspots) && media.hotspots.length > 0 && (
          <span style={{ marginLeft: 8, opacity: 0.85 }}>
            - {media.hotspots.length} hotspots
          </span>
        )}
      </div>

    </div>
  );
};

export default LiveModel3DPreview;
